import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  WaterSavedMonthDetailResponse,
  WaterSavedMonthlyListResponse,
  WaterSavedPeriodListResponse,
  WaterSavedPeriodType,
  WaterSavedRangeSensorListResponse,
  WaterSavedRecomputeResponse,
  WaterSavedSensorListResponse,
  WaterSavedValidationData,
} from '@metron/shared';
import { useApiClient } from './useApiClient.ts';

const WATER_SAVED_QUERY_KEY = ['water-saved'] as const;

/**
 * Hook to fetch all months with summary statistics (including meter stats)
 */
export function useWaterSavedMonthly() {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [...WATER_SAVED_QUERY_KEY, 'monthly'],
    queryFn: async () => {
      return await apiClient.get<WaterSavedMonthlyListResponse>('water-saved/monthly');
    },
  });
}

/**
 * Hook to fetch precomputed quarter/year summaries
 */
export function useWaterSavedPeriods(period: WaterSavedPeriodType | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [...WATER_SAVED_QUERY_KEY, 'periods', period],
    queryFn: async () => {
      return await apiClient.get<WaterSavedPeriodListResponse>(
        `water-saved/periods?period=${period}`,
      );
    },
    enabled: !!period,
  });
}

/**
 * Hook to fetch paginated sensors with fixed leaks in a [from, to) date range
 * (quarter/year drill-down)
 */
export function useWaterSavedRangeSensors(
  from: string | null,
  to: string | null,
  page: number = 1,
  limit: number = 50,
) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [...WATER_SAVED_QUERY_KEY, 'range-sensors', from, to, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        from: from!,
        to: to!,
        page: page.toString(),
        limit: limit.toString(),
      });
      return await apiClient.get<WaterSavedRangeSensorListResponse>(
        `water-saved/sensors?${params}`,
      );
    },
    enabled: !!(from && to),
  });
}

/**
 * Hook to fetch a single month's summary
 */
export function useWaterSavedMonthDetail(month: string | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [...WATER_SAVED_QUERY_KEY, 'monthly', month],
    queryFn: async () => {
      return await apiClient.get<WaterSavedMonthDetailResponse>(
        `water-saved/monthly/${month}`,
      );
    },
    enabled: !!month,
  });
}

/**
 * Hook to fetch paginated list of sensors for a month
 */
export function useWaterSavedSensors(
  month: string | null,
  page: number = 1,
  limit: number = 50,
) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [...WATER_SAVED_QUERY_KEY, 'sensors', month, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });
      return await apiClient.get<WaterSavedSensorListResponse>(
        `water-saved/monthly/${month}/sensors?${params}`,
      );
    },
    enabled: !!month,
  });
}

/**
 * Hook to fetch validation chart data for a sensor
 */
export function useWaterSavedValidation(sensorId: number | null) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: [...WATER_SAVED_QUERY_KEY, 'validation', sensorId],
    queryFn: async () => {
      return await apiClient.get<WaterSavedValidationData>(
        `water-saved/sensor/${sensorId}/validation`,
      );
    },
    enabled: !!sensorId,
  });
}

/**
 * Hook to recompute water saved data for a month
 */
export function useRecomputeWaterSaved() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ month }: { month: string }) => {
      return await apiClient.post<WaterSavedRecomputeResponse>(
        `water-saved/monthly/${month}/recompute`,
        {},
      );
    },
    onSuccess: (_data, variables) => {
      // Invalidate all water-saved queries to refetch fresh data
      queryClient.invalidateQueries({ queryKey: WATER_SAVED_QUERY_KEY });
      // Specifically invalidate the month that was recomputed
      queryClient.invalidateQueries({
        queryKey: [...WATER_SAVED_QUERY_KEY, 'monthly', variables.month],
      });
      queryClient.invalidateQueries({
        queryKey: [...WATER_SAVED_QUERY_KEY, 'sensors', variables.month],
      });
    },
  });
}
